import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { apiClient } from "../App";
import {
  ArrowLeft, FileText, Plus, Pencil, Trash2, ShieldCheck, Users,
  ExternalLink, Info,
} from "lucide-react";

const AdminDocuments = () => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [dialog, setDialog] = useState({ open: false, data: null });

  const load = async () => {
    try {
      const f = await apiClient.get("/document-folders");
      setFolders(f.data);
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const d = dialog.data;
    if (!d?.name || !d?.drive_folder_url) {
      toast.error("Name & Drive folder URL are required");
      return;
    }
    try {
      const payload = {
        name: d.name,
        description: d.description || null,
        drive_folder_url: d.drive_folder_url,
        visibility: d.visibility || "all_members",
        color: d.color || "#E6B800",
      };
      if (d.id) await apiClient.put(`/document-folders/${d.id}`, payload);
      else await apiClient.post("/document-folders", payload);
      toast.success(d.id ? "Updated" : "Folder added");
      setDialog({ open: false, data: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this folder? (the Drive folder itself is not deleted)")) return;
    try {
      await apiClient.delete(`/document-folders/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-documents-page">
      <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="admin-docs-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
      </Button>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <FileText className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-4xl tracking-tight">Manage Documents</h1>
          <p className="text-muted-foreground mt-1">Connect Google Drive folders so members can access shared files from inside Vivo. Each folder must be shared as &ldquo;Anyone with the link can view&rdquo; in Google Drive.</p>
        </div>
      </div>

      {/* Folders list */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{folders.length} folder(s)</p>
        <Button onClick={() => setDialog({ open: true, data: { visibility: "all_members" } })} data-testid="add-folder-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Folder
        </Button>
      </div>

      <div className="space-y-3">
        {folders.length === 0 && (
          <Card className="glass-card">
            <CardContent className="p-8 text-center text-muted-foreground">
              <Info className="w-8 h-8 mx-auto mb-3 opacity-50" />
              No folders added yet. Click &ldquo;Add Folder&rdquo; to connect your first Drive folder.
            </CardContent>
          </Card>
        )}
        {folders.map((f) => (
          <Card key={f.id} className="glass-card" data-testid={`admin-folder-row-${f.id}`}>
            <CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${f.color}20`, border: `1px solid ${f.color}40` }}>
                <FileText className="w-5 h-5" style={{ color: f.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{f.name}</span>
                  <Badge variant="outline" className={f.visibility === "committee" ? "bg-amber-500/10 border-amber-500/30 text-amber-400 text-[10px]" : "text-[10px]"}>
                    {f.visibility === "committee" ? <><ShieldCheck className="w-2.5 h-2.5 mr-1" />Committee only</> : <><Users className="w-2.5 h-2.5 mr-1" />All members</>}
                  </Badge>
                </div>
                {f.description && <div className="text-xs text-muted-foreground mt-0.5">{f.description}</div>}
                <div className="text-xs text-muted-foreground font-mono mt-1 truncate">{f.drive_folder_url}</div>
              </div>
              <div className="flex gap-1 items-center">
                <a href={f.drive_folder_url} target="_blank" rel="noreferrer">
                  <Button size="icon" variant="ghost" title="Open in Drive">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
                <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, data: { ...f } })} data-testid={`edit-folder-${f.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(f.id)} data-testid={`delete-folder-${f.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, data: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialog.data?.id ? "Edit" : "Add"} Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input
                value={dialog.data?.name || ""}
                onChange={(e) => setDialog({ open: true, data: { ...dialog.data, name: e.target.value } })}
                placeholder="e.g. Sheet Music, Committee Minutes"
                data-testid="folder-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={dialog.data?.description || ""}
                onChange={(e) => setDialog({ open: true, data: { ...dialog.data, description: e.target.value } })}
                placeholder="Optional short description"
              />
            </div>
            <div>
              <Label>Google Drive Folder URL *</Label>
              <Input
                value={dialog.data?.drive_folder_url || ""}
                onChange={(e) => setDialog({ open: true, data: { ...dialog.data, drive_folder_url: e.target.value } })}
                placeholder="https://drive.google.com/drive/folders/…"
                data-testid="folder-url-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Folder must be shared as &ldquo;Anyone with the link can view&rdquo;.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Visibility *</Label>
                <Select
                  value={dialog.data?.visibility || "all_members"}
                  onValueChange={(v) => setDialog({ open: true, data: { ...dialog.data, visibility: v } })}
                >
                  <SelectTrigger data-testid="folder-visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_members">All members</SelectItem>
                    <SelectItem value="committee">Committee only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Accent colour</Label>
                <Input
                  type="color"
                  value={dialog.data?.color || "#E6B800"}
                  onChange={(e) => setDialog({ open: true, data: { ...dialog.data, color: e.target.value } })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: null })}>Cancel</Button>
            <Button onClick={handleSave} data-testid="folder-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDocuments;
